import { Injectable, inject } from "@angular/core";
import { map } from "rxjs/operators";
import { ConstructAPI } from "src/app/API/constructAPI";
import { RestApiService } from "src/app/API/restapi";
import { IAPILoanRequest, LoanRequest } from "src/model/loanRequest.model";
import {
  IAPISharedLoanRequest,
  SharedLoanCompany,
  SharedLoanRequest,
} from "src/model/sharedLoan.model";
import { LoanAdapter } from "./loan.service";

interface ProfileLoanRequestOptions {
  withLoan?: boolean;
  approved?: boolean;
}

type LoanRequestOptions = ProfileLoanRequestOptions & {
  endDate?: string;
  includeUnprocessed?: boolean;
  authorized?: boolean;
  startDate?: string;
  customerId?: number;
  showUninvolved?: boolean;
};

interface SharedLoanRequestOptions {
  approved?: boolean;
  myProcessed?: boolean;
  processed?: boolean;
}

type ProcessAction = "agree" | "deny" | "unagree";

interface conflictedRequestOptions {
  icNumber?: string;
  excludeCustomerId?: number;
  approved?: boolean;
  inclUnprocessed?: boolean;
  withLoan?: false;
}

@Injectable({
  providedIn: "root",
})
export class LoanRequestAdapter {
  private loanAdapter = inject(LoanAdapter);

  adapt(item: any): LoanRequest {
    return new LoanRequest(
      item.customerId,
      item.epfAmount,
      item.epfDate,
      item.guarantors
        ?.map((t) => this.loanAdapter.adaptContact(t))
        .sort((a, b) => (a.Name > b.Name ? 1 : -1)),
      item.hasIAccount,
      item.id,
      item.loanInfo && this.loanAdapter.adapt(item.loanInfo),
      item.processedRemark,
      item.profileId,
      item.remark,
      item.requestBy,
      item.requestOn,
      item.updatedOn,
      item.approved,
      item.loanId,
      item.companyCode,
      item.icNumber,
      item.name,
      item.companyId,
      item.processedBy,
      item.processedOn,
      item.authorizedBy,
      item.processorRoleRequired,
      item.priority,
      item.verifiedBy,
      item.verifiedOn,
      item.dob,
      item.otherId,
      undefined,
      undefined,
      undefined,
      item.sharedProfileId,
      item.loanGroupId,
    );
  }

  adaptSharedLoanCompany(item: any): SharedLoanCompany {
    return new SharedLoanCompany(
      item.agreed,
      item.companyId,
      item.companyCode,
      item.group,
    );
  }

  adaptSharedLoan(item: any): SharedLoanRequest {
    const comp: SharedLoanCompany[] = item.companies?.map((t) =>
      this.adaptSharedLoanCompany(t),
    );
    return new SharedLoanRequest(
      item.authorizedBy,
      item.authorizedOn,
      comp,
      item.guarantors
        ?.map((t) => this.loanAdapter.adaptContact(t))
        .sort((a, b) => (a.Name > b.Name ? 1 : -1)),
      item.loanGroupId,
      item.loansInfo?.map((t) => {
        const l = this.adapt(t);
        if (!l.CompanyCode)
          l.CompanyCode = comp.find(
            (c) => c.CompanyId === l.CompanyId,
          ).CompanyCode;
        l.ProfileId = item.sharedProfileId;
        return l;
      }),
      item.processorRoleRequired,
      item.requestBy,
      item.requestOn,
      item.sharedProfileId,
      item.name,
    );
  }

  toAPIModel(loanRequest: LoanRequest, formJId?: number): IAPILoanRequest {
    return {
      profileId: loanRequest.ProfileId,
      ...(loanRequest.CompanyId && { companyId: loanRequest.CompanyId }),
      ...(loanRequest.Guarantors &&
        loanRequest.Guarantors.length > 0 && {
          guarantorIds: loanRequest.Guarantors.map((g) => g.ID),
        }),
      loanInfo:
        loanRequest.LoanInfo &&
        this.loanAdapter.toAPIModel(loanRequest.LoanInfo),
      remark: loanRequest.Remark,
      ...(formJId && { formjId: formJId }),
    };
  }

  toAPIModelSharedLoan(
    sharedLoanRequest: SharedLoanRequest,
    formJId: number,
  ): IAPISharedLoanRequest {
    return {
      profileId: sharedLoanRequest.SharedProfileId,
      guarantorIds: sharedLoanRequest.Guarantors.map((g) => g.ID),
      requests: sharedLoanRequest.LoanRequestsInfo.map((l) =>
        this.toAPIModel(l),
      ),
      formjId: formJId,
    };
  }
}

@Injectable({
  providedIn: "root",
})
export class LoanRequestService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(LoanRequestAdapter);

  getAllLoanApprovalRequest(o?: LoanRequestOptions): Promise<LoanRequest[]> {
    return new Promise<LoanRequest[]>((resolve, reject) => {
      this.api
        .get<LoanRequest[]>(
          this.constructApi.getAllLoanApprovalRequest(
            o?.endDate,
            o?.withLoan,
            o?.approved,
            o?.includeUnprocessed,
            o?.authorized,
            o?.startDate,
            o?.customerId,
            o?.showUninvolved
          ),
        )
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  getLoanApprovalRequestByIds(ids: number[]): Promise<LoanRequest[]> {
    return new Promise<LoanRequest[]>((resolve, reject) => {
      this.api
        .get<LoanRequest[]>(this.constructApi.getLoanApprovalRequestByIds(ids))
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  getLoanApprovalRequestById(id: number): Promise<LoanRequest> {
    return new Promise<LoanRequest>((resolve, reject) => {
      this.api
        .get<LoanRequest>(this.constructApi.getLoanApprovalRequestById(id))
        .pipe(map((data) => this.adapter.adapt(data)))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            if (err.status !== 404) {
              this.api.handleError(err);
              console.error(err);
            }
            reject(err);
          },
        });
    });
  }

  getLoanGroupApprovalRequests(groupId: number): Promise<LoanRequest[]> {
    return new Promise<LoanRequest[]>((resolve, reject) => {
      this.api
        .get<LoanRequest[]>(
          this.constructApi.getLoanGroupApprovalRequests(groupId),
        )
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  getAllSharedLoanApprovalRequest(
    o?: SharedLoanRequestOptions,
  ): Promise<SharedLoanRequest[]> {
    return new Promise<SharedLoanRequest[]>((resolve, reject) => {
      this.api
        .get<SharedLoanRequest[]>(
          this.constructApi.getAllSharedLoanApprovalRequest(
            o?.approved,
            o?.myProcessed,
            o?.processed,
          ),
        )
        .pipe(map((data) => data.map((d) => this.adapter.adaptSharedLoan(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  getProfileLoanApprovalRequest(
    profileId: number,
    o?: ProfileLoanRequestOptions,
  ): Promise<LoanRequest[]> {
    return new Promise<LoanRequest[]>((resolve, reject) => {
      this.api
        .get<LoanRequest[]>(
          this.constructApi.getProfileLoanApprovalRequest(
            profileId,
            o?.approved,
            o?.withLoan,
          ),
        )
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  postLoanRequest(loanRequest: LoanRequest, formJId: number, elevate = false) {
    const url = elevate
      ? this.constructApi.getAddElevatedEPFLoanRequest()
      : this.constructApi.getAddLoanRequest();
    return new Promise<LoanRequest>((resolve, reject) => {
      this.api
        .post(url, this.adapter.toAPIModel(loanRequest, formJId))
        .subscribe({
          next: (response) => resolve(this.adapter.adapt(response)),
          error: (err) => {
            if (!err.error?.requireElevation) {
              console.error(err);
              this.api.handleError(err);
            }
            reject(err);
          },
        });
    });
  }

  postSharedLoanRequest(
    sharedLoanRequest: SharedLoanRequest,
    formJId: number,
    elevate = false,
  ): Promise<SharedLoanRequest> {
    const url = elevate
      ? this.constructApi.getAddSharedElevatedEPFLoanRequest()
      : this.constructApi.getAddSharedLoanRequest();
    return new Promise<SharedLoanRequest>((resolve, reject) => {
      this.api
        .post(
          url,
          this.adapter.toAPIModelSharedLoan(sharedLoanRequest, formJId),
        )
        .subscribe({
          next: (response) => resolve(this.adapter.adaptSharedLoan(response)),
          error: (err) => {
            if (!err.error?.requireElevation) {
              console.error(err);
              this.api.handleError(err);
            }
            reject(err);
          },
        });
    });
  }

  postProcessSharedLoanAgreement(
    loanGroupId: number,
    action: ProcessAction,
    remark?: string,
  ): Promise<boolean> {
    let data;
    if (remark?.length > 0) data = { remark: remark };
    return new Promise<boolean>((resolve, reject) => {
      this.api
        .post(this.constructApi.getSharedLoanProcess(loanGroupId, action), data)
        .subscribe({
          next: (response) => resolve(response !== undefined),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  findConflictedTicketCustomer(
    o?: conflictedRequestOptions,
  ): Promise<LoanRequest[]> {
    return new Promise<LoanRequest[]>((resolve, reject) => {
      this.api
        .get<LoanRequest[]>(
          this.constructApi.getSearchConflictedTicketCustomer(
            o?.icNumber,
            o?.excludeCustomerId,
            o?.approved,
            o?.inclUnprocessed,
            o?.withLoan,
          ),
        )
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            console.error(err);
            this.api.handleError(err);
            reject(err);
          },
        });
    });
  }

  processLoanRequest(id: number, remark: string, approve: boolean) {
    return new Promise<boolean>((resolve, reject) => {
      const api = approve
        ? this.constructApi.getApproveLoanApprovalRequest(id)
        : this.constructApi.getDenyLoanApprovalRequest(id);
      this.api.post<LoanRequest>(api, { remark: remark }).subscribe({
        next: (response) => {
          // resolve(this.adapter.adapt(response));
          resolve(true);
        },
        error: (err) => {
          this.api.handleError(err);
          console.error("Failed to process loan request: ", err);
          reject(err);
        },
      });
    });
  }

  processSharedLoanRequest(lgId: number, remark: string, approve: boolean) {
    return new Promise<boolean>((resolve, reject) => {
      const api = approve
        ? this.constructApi.getApproveGroupLoanApprovalRequest(lgId)
        : this.constructApi.getDenyGroupLoanApprovalRequest(lgId);
      this.api.post<LoanRequest>(api, { remark: remark }).subscribe({
        next: (response) => {
          // resolve(this.adapter.adapt(response));
          resolve(true);
        },
        error: (err) => {
          this.api.handleError(err);
          console.error("Failed to approve group loan request: ", err);
          reject(err);
        },
      });
    });
  }

  async processMultipleSharedLoanRequest(
    loanGroups: { lgId: number; approve: boolean; remark: string }[],
  ) {
    const first = loanGroups[0];
    const others = loanGroups.slice(1);

    const results = [];

    results.push(
      await this.processSharedLoanRequest(
        first.lgId,
        first.remark,
        first.approve,
      ),
    );

    for (const t of others) {
      const res = await this.processSharedLoanRequest(
        t.lgId,
        t.remark,
        t.approve,
      );
      results.push(res);
    }
    return results;
  }
}
